import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LanguageThemeSelector } from '../components/common';
import SystemNotificationsMenu from '../components/common/SystemNotificationsMenu';
import Logo from '../components/Logo/Logo';
import { AddStoreModal } from '../components/Store';
import { SupportChatList } from '../components/Support';
import api from '../services/api';
import Profile from '../components/Dashboard/Profile';
import ChangePassword from '../components/Dashboard/ChangePassword';
import AiAssistantTopic from './topics/AiAndSupportTopics';
import StoresTopic from './topics/StoresTopic';
import BalanceTopic from './topics/BalanceTopic';
import {
  DeductionsReport,
  ReturnsReport,
  SalesReport,
  DailyActivityReport,
} from './topics/ReportsTopics';
import { SUPPORT_UNREAD_POLL_INTERVAL_MS } from '../constants/polling';
import './ChatApp.css';

const CHAT_MODE = {
  support: 'support',
  ai: 'ai',
};

const SECTION = {
  chat: 'chat',
  stores: 'stores',
  balance: 'balance',
  billing: 'billing',
  pendingDecision: 'pendingDecision',
  warehouse: 'warehouse',
  returnsInfo: 'returnsInfo',
  dailyReport: 'dailyReport',
  profile: 'profile',
  security: 'security',
};

const FILL_HEIGHT_SECTIONS = new Set([
  SECTION.chat,
  SECTION.stores,
  SECTION.warehouse,
  SECTION.returnsInfo,
  SECTION.pendingDecision,
]);

const BOTTOM_NAV = [
  { id: SECTION.stores, icon: '🏪', labelKey: 'navStores' },
  { id: SECTION.balance, icon: '💰', labelKey: 'navBalance' },
  { id: SECTION.billing, icon: '📉', labelKey: 'navBilling' },
  { id: SECTION.pendingDecision, icon: '⏳', labelKey: 'navPendingDecision', badgeKey: 'pending' },
  { id: SECTION.warehouse, icon: '📦', labelKey: 'navWarehouse' },
  { id: SECTION.returnsInfo, icon: '↩️', labelKey: 'navReturnsInfo' },
  { id: SECTION.dailyReport, icon: '📋', labelKey: 'reportDaily' },
];

function NavBadge({ count }) {
  if (!count || count <= 0) return null;
  const display = count > 99 ? '99+' : String(count);
  return <span className="chat-app-nav-badge">{display}</span>;
}

function ChatSwitchIcon({ variant }) {
  if (variant === 'support') {
    return (
      <span className="chat-app-chat-switch-icon" aria-hidden>
        <i className="bi bi-headset" />
      </span>
    );
  }
  return (
    <span className="chat-app-chat-switch-icon chat-app-chat-switch-icon--cat" aria-hidden>
      🐱
    </span>
  );
}

export default function ChatAppShell({ stores: externalStores }) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [activeSection, setActiveSection] = useState(SECTION.chat);
  const [chatMode, setChatMode] = useState(CHAT_MODE.ai);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [stores, setStores] = useState(externalStores || []);
  const [deductionsPrefill, setDeductionsPrefill] = useState(null);
  const [returnsPrefill, setReturnsPrefill] = useState(null);
  const [pendingDecisionCount, setPendingDecisionCount] = useState(0);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [showAddStore, setShowAddStore] = useState(false);
  const noop = useCallback(() => {}, []);

  const refreshStores = useCallback(async () => {
    try {
      const r = await api.getStores();
      setStores(Array.isArray(r) ? r : []);
    } catch {
      setStores([]);
    }
  }, []);

  const handleNavigateDeductions = useCallback((nav) => {
    setDeductionsPrefill({
      from: nav.date_from,
      to: nav.date_to,
      type: nav.type || '',
    });
    setActiveSection(SECTION.billing);
  }, []);

  const handleNavigateReturns = useCallback((nav) => {
    setReturnsPrefill({
      subTab: nav.subTab || 'pendingDecision',
    });
    setActiveSection(nav.subTab === 'pendingDecision' ? SECTION.pendingDecision : SECTION.returnsInfo);
  }, []);

  useEffect(() => {
    if (Array.isArray(externalStores) && externalStores.length) {
      setStores(externalStores);
      return;
    }
    refreshStores();
  }, [externalStores, refreshStores]);

  const storeExternalId = useMemo(() => {
    if (!selectedStoreId) return null;
    const s = stores.find((x) => x.id === selectedStoreId);
    return s?.store_id || null;
  }, [selectedStoreId, stores]);

  const loadPendingDecisionCount = useCallback(async () => {
    const statsParams = storeExternalId ? { store_id: storeExternalId } : {};
    try {
      const statsRes = await api.getReturnsStatistics(statsParams);
      const stats = statsRes?.data ?? statsRes;
      setPendingDecisionCount(Number(stats?.pending_decision) || 0);
    } catch {
      setPendingDecisionCount(0);
    }
  }, [storeExternalId]);

  /** Бейдж в шапке: SupportChatList передаёт уже посчитанное значение без повторного GET. */
  const handleSupportUnreadBadge = useCallback((displayCount) => {
    if (typeof displayCount === 'number' && !Number.isNaN(displayCount)) {
      setSupportUnreadCount(Math.max(0, displayCount));
    }
  }, []);

  useEffect(() => {
    loadPendingDecisionCount();
    const timer = setInterval(loadPendingDecisionCount, 60_000);
    return () => clearInterval(timer);
  }, [loadPendingDecisionCount]);

  // Когда не на экране поддержки — редкий опрос для бейджа в переключателе чатов.
  useEffect(() => {
    const onSupportScreen = activeSection === SECTION.chat && chatMode === CHAT_MODE.support;
    if (onSupportScreen) return undefined;

    const pollUnread = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const unreadRes = await api.getSupportChatUnreadCount();
        const unread = unreadRes?.data ?? unreadRes;
        setSupportUnreadCount(Number(unread?.unread_count ?? unread?.count) || 0);
      } catch {
        setSupportUnreadCount(0);
      }
    };

    pollUnread();
    const timer = setInterval(pollUnread, SUPPORT_UNREAD_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [activeSection, chatMode]);

  const sectionTitle = {
    [SECTION.chat]: chatMode === CHAT_MODE.support ? t('chatApp.chatSupport') : t('chatApp.topicAi'),
    [SECTION.stores]: t('chatApp.navStores'),
    [SECTION.balance]: t('chatApp.navBalance'),
    [SECTION.billing]: t('chatApp.navBilling'),
    [SECTION.pendingDecision]: t('chatApp.navPendingDecision'),
    [SECTION.warehouse]: t('chatApp.navWarehouse'),
    [SECTION.returnsInfo]: t('chatApp.navReturnsInfo'),
    [SECTION.dailyReport]: t('chatApp.reportDaily'),
    [SECTION.profile]: t('chatApp.topicProfile'),
    [SECTION.security]: t('security.title'),
  }[activeSection];

  const goToChat = (mode) => {
    setChatMode(mode);
    setActiveSection(SECTION.chat);
  };

  const renderBody = () => {
    switch (activeSection) {
      case SECTION.chat:
        if (chatMode === CHAT_MODE.support) {
          return (
            <div className="chat-app-support-embed">
              <SupportChatList
                embeddedInChatApp
                isAdmin={false}
                onUnreadCountChange={handleSupportUnreadBadge}
                onNotificationAdd={noop}
                onSelectedChatChange={noop}
              />
            </div>
          );
        }
        return (
          <AiAssistantTopic
            storeId={storeExternalId}
            stores={stores}
            onNavigateDeductions={handleNavigateDeductions}
            onNavigateReturns={handleNavigateReturns}
          />
        );
      case SECTION.stores:
        return <StoresTopic onStoresChange={setStores} />;
      case SECTION.dailyReport:
        return (
          <DailyActivityReport
            storeId={storeExternalId}
            stores={stores}
            onNavigateDeductions={handleNavigateDeductions}
            onNavigateReturns={handleNavigateReturns}
          />
        );
      case SECTION.billing:
        return (
          <DeductionsReport
            storeId={storeExternalId}
            prefill={deductionsPrefill}
            onPrefillConsumed={() => setDeductionsPrefill(null)}
          />
        );
      case SECTION.balance:
        return (
          <BalanceTopic
            storePk={selectedStoreId}
            storeExternalId={storeExternalId}
            onOpenSupport={() => goToChat(CHAT_MODE.support)}
          />
        );
      case SECTION.warehouse:
        return <SalesReport selectedStoreId={storeExternalId} />;
      case SECTION.returnsInfo:
        return (
          <ReturnsReport
            selectedStoreId={storeExternalId}
            prefill={returnsPrefill}
            onPrefillConsumed={() => setReturnsPrefill(null)}
          />
        );
      case SECTION.pendingDecision:
        return (
          <ReturnsReport
            selectedStoreId={storeExternalId}
            prefill={{ subTab: 'pendingDecision' }}
            onPrefillConsumed={() => {}}
          />
        );
      case SECTION.profile:
        return <Profile embedded />;
      case SECTION.security:
        return <ChangePassword embedded />;
      default:
        return null;
    }
  };

  const getNavBadge = (badgeKey) => {
    if (badgeKey === 'pending') return pendingDecisionCount;
    return 0;
  };

  return (
    <div className="chat-app-outer">
      <div className={`chat-app chat-app--v2${activeSection === SECTION.stores ? ' chat-app--stores-open' : ''}`}>
        <header className="chat-app-topbar">
          <div className="chat-app-topbar-left">
            <button
              type="button"
              className="chat-app-brand"
              onClick={() => goToChat(CHAT_MODE.ai)}
              title={t('chatApp.switchChat')}
            >
              <Logo size="small" />
            </button>
          </div>

          <div className="chat-app-topbar-center">
            <div className="chat-app-chat-switch" role="tablist" aria-label={t('chatApp.chatSwitchLabel')}>
              <button
                type="button"
                role="tab"
                aria-selected={activeSection === SECTION.chat && chatMode === CHAT_MODE.support}
                className={`chat-app-chat-switch-btn${
                  activeSection === SECTION.chat && chatMode === CHAT_MODE.support ? ' active' : ''
                }`}
                onClick={() => goToChat(CHAT_MODE.support)}
              >
                <span className="chat-app-chat-switch-inner">
                  <ChatSwitchIcon variant="support" />
                  <span className="chat-app-chat-switch-label">{t('chatApp.chatSupport')}</span>
                </span>
                <NavBadge count={supportUnreadCount} />
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeSection === SECTION.chat && chatMode === CHAT_MODE.ai}
                className={`chat-app-chat-switch-btn${
                  activeSection === SECTION.chat && chatMode === CHAT_MODE.ai ? ' active' : ''
                }`}
                onClick={() => goToChat(CHAT_MODE.ai)}
              >
                <span className="chat-app-chat-switch-inner">
                  <ChatSwitchIcon variant="cat" />
                  <span className="chat-app-chat-switch-label">{t('chatApp.topicAi')}</span>
                </span>
              </button>
            </div>
          </div>

          <div className="chat-app-topbar-right">
            <div className="chat-app-store-controls">
              <Dropdown className="chat-app-store-dropdown">
                <Dropdown.Toggle variant="outline-secondary" size="sm" className="chat-app-store-toggle">
                  {selectedStoreId
                    ? stores.find((s) => s.id === selectedStoreId)?.name || t('nav.allStores')
                    : t('nav.allStores')}
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  <Dropdown.Item active={!selectedStoreId} onClick={() => setSelectedStoreId(null)}>
                    {t('nav.allStores')}
                  </Dropdown.Item>
                  {stores.map((s) => (
                    <Dropdown.Item
                      key={s.id}
                      active={selectedStoreId === s.id}
                      onClick={() => setSelectedStoreId(s.id)}
                    >
                      {s.name || s.store_id}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              <Button
                variant="outline-primary"
                size="sm"
                className="chat-app-add-store-btn"
                onClick={() => setShowAddStore(true)}
                title={t('nav.addStore')}
              >
                <i className="bi bi-plus-lg" />
                <span className="chat-app-add-store-label">{t('nav.addStore')}</span>
              </Button>
            </div>
            <LanguageThemeSelector />
            {!user?.is_superuser && (
              <div className="chat-app-notify-wrap">
                <SystemNotificationsMenu
                  stores={stores}
                  onOpenApiKeyModal={() => setActiveSection(SECTION.stores)}
                />
              </div>
            )}
            <Dropdown align="end" className="chat-app-profile-dropdown">
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                className="chat-app-profile-toggle"
                title={user?.email || t('chatApp.topicProfile')}
              >
                <span className="chat-app-profile-icon" aria-hidden>👤</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Header className="text-truncate" style={{ maxWidth: 220 }}>
                  {user?.email}
                </Dropdown.Header>
                <Dropdown.Item
                  active={activeSection === SECTION.profile}
                  onClick={() => setActiveSection(SECTION.profile)}
                >
                  {t('chatApp.topicProfile')}
                </Dropdown.Item>
                <Dropdown.Item
                  active={activeSection === SECTION.security}
                  onClick={() => setActiveSection(SECTION.security)}
                >
                  {t('security.title')}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setActiveSection(SECTION.dailyReport)}>
                  {t('chatApp.reportDaily')}
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={logout}>{t('nav.signOut')}</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </header>

        {activeSection !== SECTION.chat
          && activeSection !== SECTION.pendingDecision
          && sectionTitle && (
          <div className="chat-app-section-title-bar">
            <strong>{sectionTitle}</strong>
          </div>
        )}

        <div className={`chat-app-workspace${activeSection === SECTION.stores ? ' chat-app-workspace--stores' : ''}`}>
          <div
            className={`chat-app-main-body${
              FILL_HEIGHT_SECTIONS.has(activeSection) ? ' chat-app-main-body--fill' : ''
            }${activeSection === SECTION.stores ? ' chat-app-main-body--stores' : ''}`}
          >
            {renderBody()}
          </div>
        </div>

        <footer className="chat-app-bottom-dock">
          <nav className="chat-app-bottom-nav" aria-label={t('chatApp.bottomNavLabel')}>
            {BOTTOM_NAV.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`chat-app-bottom-nav-item${activeSection === item.id ? ' active' : ''}`}
                onClick={() => setActiveSection(item.id)}
                title={t(`chatApp.${item.labelKey}`)}
              >
                <span className="chat-app-bottom-nav-icon" aria-hidden>{item.icon}</span>
                <span className="chat-app-bottom-nav-label">{t(`chatApp.${item.labelKey}`)}</span>
                {item.badgeKey && <NavBadge count={getNavBadge(item.badgeKey)} />}
              </button>
            ))}
          </nav>
        </footer>

        <AddStoreModal
          show={showAddStore}
          onHide={() => setShowAddStore(false)}
          onStoreAdded={(store) => {
            setStores((prev) => [...prev, store]);
            setShowAddStore(false);
          }}
        />
      </div>
    </div>
  );
}
